import { toPayloadIsoDateTime } from '../dateTime';
import { createI18nStringArrayFields, createI18nStringFields } from '../i18nFields';
import type { PayloadValidationResult } from '../types';

import { STATUS_MATCH_EVIDENCE_BASE, buildChallengeCardSubtitle, buildChallengeCardTitle } from './challengeCard';
import { PREVIEW_REQUIRED } from './constants';
import { resolveProgramDisplayName } from './programs';
import { normalizeCampaignSlug } from './slug';
import { validateStatusClaimPayload } from './statusClaimValidation';
import { allStringsNonEmpty, validateStatusMatchPayload } from './statusMatchValidation';
import type { ChallengeCardBackground, ProgramConfig, StatusCampaignPayloadBuilderState } from './types';

function previewValue(value: string): string {
  return value.trim() ? value.trim() : PREVIEW_REQUIRED;
}

function previewStringArray(values: Array<string>): Array<string> {
  return allStringsNonEmpty(values) ? values.map((value) => value.trim()) : [PREVIEW_REQUIRED];
}

function previewChallengeCardBackground(background: ChallengeCardBackground): Record<string, unknown> {
  if (background.type === 'image') {
    return {
      type: 'image',
      imageId: previewValue(background.imageId ?? ''),
    };
  }
  if (background.type === 'tierGradient') {
    return { type: 'tierGradient' };
  }
  return {
    type: 'color',
    value: previewValue(background.value ?? ''),
  };
}

export function validatePayloadState(state: StatusCampaignPayloadBuilderState): PayloadValidationResult {
  if (state.mode === 'statusClaim') {
    return validateStatusClaimPayload(state);
  }

  return validateStatusMatchPayload(state);
}

function mapProgramForPreview(program: ProgramConfig): Record<string, unknown> {
  const programName = program.programName.trim();
  const tiers = program.tierMappings
    .filter((mapping) => mapping.sourceTier.trim() || mapping.targetSocieteTier.trim())
    .map((mapping) => ({
      sourceTier: previewValue(mapping.sourceTier),
      targetSocieteTier: previewValue(mapping.targetSocieteTier),
    }));

  return {
    name: previewValue(programName ? resolveProgramDisplayName(programName) : ''),
    logoImageId: previewValue(program.logoImageId),
    tiers: tiers.length > 0 ? tiers : [{ sourceTier: PREVIEW_REQUIRED, targetSocieteTier: PREVIEW_REQUIRED }],
  };
}

/** Builds a JSON-shaped object for preview, marking missing required values as `[required]`. */
export function buildPreviewPayload(state: StatusCampaignPayloadBuilderState): Record<string, unknown> {
  if (state.mode === 'statusClaim') {
    return {
      campaignId: previewValue(state.campaignId),
      slug: previewValue(normalizeCampaignSlug(state.slug)),
      termsSlug: previewValue(state.termsSlug),
      hero: {
        ...createI18nStringFields('title', previewValue(state.heroTitle)),
        ...createI18nStringFields('subtitle', previewValue(state.heroSubtitle)),
        imageId: previewValue(state.heroImageId),
      },
      partner: {
        name: previewValue(state.partnerName),
        logoImageId: previewValue(state.partnerLogoImageId),
        ...createI18nStringFields('memberStatus', 'member'),
      },
      tiers: state.tiersGranted.length > 0 ? [...state.tiersGranted] : [PREVIEW_REQUIRED],
      secondarySection: {
        ...createI18nStringFields('title', state.secondaryTitle.trim()),
        ...createI18nStringFields('subtitle', state.secondarySubtitle.trim()),
      },
    };
  }

  const programs =
    state.programs.length > 0
      ? state.programs.map(mapProgramForPreview)
      : [
          {
            name: PREVIEW_REQUIRED,
            logoImageId: PREVIEW_REQUIRED,
            tiers: [{ sourceTier: PREVIEW_REQUIRED, targetSocieteTier: PREVIEW_REQUIRED }],
          },
        ];

  const conditions =
    state.conditions.length > 0
      ? state.conditions.map((condition) => ({
          icon: condition.icon,
          ...createI18nStringFields('title', previewValue(condition.title)),
          ...createI18nStringFields('description', previewValue(condition.description)),
        }))
      : [
          {
            icon: 'clock',
            ...createI18nStringFields('title', PREVIEW_REQUIRED),
            ...createI18nStringFields('description', PREVIEW_REQUIRED),
          },
        ];

  const howItWorks =
    state.howItWorks.length > 0
      ? state.howItWorks.map((step) => ({
          ...createI18nStringFields('title', previewValue(step.title)),
          ...createI18nStringFields('description', previewValue(step.description)),
        }))
      : [
          {
            ...createI18nStringFields('title', PREVIEW_REQUIRED),
            ...createI18nStringFields('description', PREVIEW_REQUIRED),
          },
        ];

  const terms = state.terms.filter(Boolean);
  const cardTitle = buildChallengeCardTitle(previewValue(state.challengeCardTitleSuffix));
  const cardSubtitle = buildChallengeCardSubtitle(
    previewValue(state.challengeCardSubtitleBefore),
    previewValue(state.challengeCardSubtitleAfter),
  );

  return {
    slug: previewValue(normalizeCampaignSlug(state.slug)),
    campaignId: previewValue(state.campaignId),
    allowedRegions:
      state.allowedRegions !== undefined && state.allowedRegions.length > 0
        ? [...state.allowedRegions]
        : [PREVIEW_REQUIRED],
    applicationPeriod: {
      startsAt: toPayloadIsoDateTime(state.applicationPeriodFrom),
      endsAt: toPayloadIsoDateTime(state.applicationPeriodTo),
    },
    hero: {
      ...createI18nStringFields('title', previewValue(state.heroTitle)),
      ...createI18nStringFields('subtitle', previewValue(state.heroSubtitle)),
      ...createI18nStringFields('description', previewValue(state.heroDescription)),
      ...createI18nStringFields('ctaText', previewValue(state.heroCtaText)),
      imageId: previewValue(state.heroImageId),
    },
    programs,
    evidence: {
      ...STATUS_MATCH_EVIDENCE_BASE,
      minPhotos: state.evidenceMinPhotos > 0 ? state.evidenceMinPhotos : PREVIEW_REQUIRED,
      ...createI18nStringArrayFields('photoHints', previewStringArray(state.evidencePhotoHints)),
      acceptedFormats: previewStringArray(state.evidenceAcceptedFormats),
      ...createI18nStringArrayFields('proofRequirements', previewStringArray(state.evidenceProofRequirements)),
      verificationImageId: previewValue(state.evidenceVerificationImageId),
    },
    conditions,
    howItWorks,
    challengeCard: {
      background: previewChallengeCardBackground(state.challengeCardBackground),
      ...createI18nStringFields('cardTitle', cardTitle),
      ...createI18nStringFields('cardSubtitle', cardSubtitle),
    },
    successSteps: state.successSteps.map((step) => ({
      key: step.key,
      ...createI18nStringFields('title', previewValue(step.title)),
      ...createI18nStringFields('description', previewValue(step.description)),
    })),
    faqs: state.faqs
      .filter((faq) => faq.question || faq.answer)
      .map((faq) => ({
        ...createI18nStringFields('question', faq.question),
        ...createI18nStringFields('answer', faq.answer),
      })),
    ...createI18nStringArrayFields('terms', terms),
    termsSlug: previewValue(state.termsSlug),
  };
}

export function serializePreviewPayload(state: StatusCampaignPayloadBuilderState): string {
  return JSON.stringify(buildPreviewPayload(state), null, 2);
}
